import { z } from "zod";

export const OnboardingTemplateParticipationModeZ = z.enum(["SystemAssisted", "StaffReview"]);
export type OnboardingTemplateParticipationMode = z.infer<typeof OnboardingTemplateParticipationModeZ>;

export const OnboardingTemplateStatusZ = z.enum(["Active", "Retired"]);
export type OnboardingTemplateStatus = z.infer<typeof OnboardingTemplateStatusZ>;

export const OnboardingTemplateVersionStateZ = z.enum(["Draft", "Published"]);
export type OnboardingTemplateVersionState = z.infer<typeof OnboardingTemplateVersionStateZ>;

export const OnboardingTemplateFeeCategoryZ = z.object({
  name: z.string(),
  currency: z.string(),
  fixedAmount: z.number(),
});
export type OnboardingTemplateFeeCategory = z.infer<typeof OnboardingTemplateFeeCategoryZ>;

export const OnboardingTemplateTeamMemberZ = z.object({
  userId: z.guid().nullable().default(null),
  email: z.string().nullable().default(null),
  isLeader: z.boolean(),
});
export type OnboardingTemplateTeamMember = z.infer<typeof OnboardingTemplateTeamMemberZ>;

export const OnboardingTemplateDocumentsZ = z.object({
  conflicts: z.array(z.guid()),
  vendorPolicies: z.array(z.guid()),
});
export type OnboardingTemplateDocuments = z.infer<typeof OnboardingTemplateDocumentsZ>;

export const OnboardingTemplateTermsZ = z.object({
  version: z.string(),
  participationMode: OnboardingTemplateParticipationModeZ,
  workflowVersion: z.number().int(),
  name: z.string(),
  description: z.string(),
  effectiveDatePolicy: z.string(),
  endDatePolicy: z.string(),
  negotiable: z.boolean(),
  conflictsCheckWaived: z.boolean(),
  discount: z.number(),
  feeSchedule: z.array(OnboardingTemplateFeeCategoryZ),
  vendorTeam: z.array(OnboardingTemplateTeamMemberZ).default([]),
  documents: OnboardingTemplateDocumentsZ.default({ conflicts: [], vendorPolicies: [] }),
});
export type OnboardingTemplateTerms = z.infer<typeof OnboardingTemplateTermsZ>;

export const CreateOnboardingTemplateRequestZ = z.object({
  templateId: z.string(),
  displayName: z.string(),
  draft: OnboardingTemplateTermsZ,
});
export type CreateOnboardingTemplateRequest = z.infer<typeof CreateOnboardingTemplateRequestZ>;

export const CreateOnboardingTemplateDraftRequestZ = z.object({
  sourceVersion: z.string(),
  newVersion: z.string(),
});
export type CreateOnboardingTemplateDraftRequest = z.infer<typeof CreateOnboardingTemplateDraftRequestZ>;

export const OnboardingTemplateValidationErrorZ = z.object({
  path: z.string(),
  message: z.string(),
});
export type OnboardingTemplateValidationError = z.infer<typeof OnboardingTemplateValidationErrorZ>;

export const OnboardingTemplateValidationResponseZ = z.object({
  isValid: z.boolean(),
  errors: z.array(OnboardingTemplateValidationErrorZ),
});
export type OnboardingTemplateValidationResponse = z.infer<typeof OnboardingTemplateValidationResponseZ>;

export const OnboardingTemplateManagementListItemZ = z.object({
  templateId: z.string(),
  displayName: z.string(),
  status: OnboardingTemplateStatusZ,
  isDefault: z.boolean(),
  currentPublishedVersion: z.string().nullable(),
  draftVersion: z.string().nullable(),
  participationMode: OnboardingTemplateParticipationModeZ.nullable(),
  workflowVersion: z.number().int().nullable(),
  negotiable: z.boolean().nullable(),
  updatedByUserId: z.guid(),
  updatedDate: z.iso.datetime({ offset: true }),
  revision: z.number().int().nonnegative(),
});
export type OnboardingTemplateManagementListItem = z.infer<typeof OnboardingTemplateManagementListItemZ>;

export const OnboardingTemplateVersionManagementZ = OnboardingTemplateTermsZ.extend({
  state: OnboardingTemplateVersionStateZ,
  vendorDisplayName: z.string(),
  createdByUserId: z.guid(),
  createdDate: z.iso.datetime({ offset: true }),
  publishedByUserId: z.guid().nullable(),
  publishedDate: z.iso.datetime({ offset: true }).nullable(),
  revision: z.number().int().nonnegative(),
});
export type OnboardingTemplateVersionManagement = z.infer<typeof OnboardingTemplateVersionManagementZ>;

export const OnboardingTemplateManagementDetailZ = z.object({
  definition: OnboardingTemplateManagementListItemZ,
  versions: z.array(OnboardingTemplateVersionManagementZ),
  draft: OnboardingTemplateVersionManagementZ.nullable(),
});
export type OnboardingTemplateManagementDetail = z.infer<typeof OnboardingTemplateManagementDetailZ>;

export const OnboardingTemplateManagementListZ = z.array(OnboardingTemplateManagementListItemZ);
